import { useEffect, useMemo, useState } from 'react'
import { apiJson, ApiError } from '../api'
import type { ThemeTokens } from '../theme'
import { pageStyles } from '../styles'
import { DataTable } from '../components/DataTable'
import type { DataTableColumn } from '../components/DataTable'
import { StatCard } from '../components/StatCard'
import { DirectoryPicker, dirEmail } from '../components/DirectoryPicker'
import type { DirectoryUser } from '../components/DirectoryPicker'

/**
 * Report scheduling: who gets the Underwriting Team View, how often, and
 * what actually happened on each send. Follows BudgetRequests.tsx's own
 * list+expand shape for the Runs tab, and Keys.tsx's row-detail drawer
 * (`styles.modalOverlay`/`modalCard`) for create/edit -- no new UI pattern
 * invented for this page.
 */

export interface ReportParamSpec {
  key: string
  label: string
  type: string
  options: string[]
}

export interface ReportCatalogEntry {
  id: string
  label: string
  description: string
  dwhView: string
  params: ReportParamSpec[]
}

export type ReportFrequency = 'daily' | 'weekdays' | 'weekly'

export interface ReportScheduleRow {
  id: number
  reportId: string
  params: Record<string, string>
  recipients: string[]
  frequency: ReportFrequency
  dayOfWeek: number | null
  timeOfDay: string
  timezone: string
  enabled: boolean
  schedulerArn: string | null
  createdBy: string
  createdAt: string
  updatedBy: string | null
  updatedAt: string | null
}

export interface ReportRunRow {
  id: number
  scheduleId: number | null
  reportId: string
  params: Record<string, string>
  startedAt: string
  finishedAt: string | null
  status: 'running' | 'ok' | 'partial' | 'error'
  uid: string | null
  url: string | null
  recipientsSent: string[]
  recipientsFailed: Record<string, string>
  triggeredBy: string
  correlationId: string | null
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (HTTP ${err.status})`
  return err instanceof Error ? err.message : String(err)
}

function fmtDate(iso: string | null): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function cadenceLabel(s: Pick<ReportScheduleRow, 'frequency' | 'dayOfWeek' | 'timeOfDay' | 'timezone'>): string {
  const time = s.timeOfDay.slice(0, 5)
  const when = s.frequency === 'weekly' && s.dayOfWeek !== null ? DAY_NAMES[s.dayOfWeek] : s.frequency === 'weekdays' ? 'Weekdays' : 'Daily'
  return `${when} · ${time} ${s.timezone}`
}

const STATUS_TONE: Record<ReportRunRow['status'], { bg: string; fg: string }> = {
  running: { bg: '#E7F2FF', fg: '#255A91' },
  ok: { bg: '#D9F2E4', fg: '#0D6B3F' },
  partial: { bg: '#FFF4D6', fg: '#8A6D00' },
  error: { bg: '#FBDCE0', fg: '#9A1F35' },
}

function StatusBadge({ status, tokens }: { status: ReportRunRow['status']; tokens: ThemeTokens }) {
  const tone = STATUS_TONE[status]
  const styles = pageStyles(tokens)
  return <span style={{ ...styles.badge, background: tone.bg, color: tone.fg }}>{status}</span>
}

function EnabledBadge({ enabled, tokens }: { enabled: boolean; tokens: ThemeTokens }) {
  const styles = pageStyles(tokens)
  return (
    <span style={{ ...styles.badge, background: enabled ? '#D9F2E4' : tokens.border, color: enabled ? '#0D6B3F' : tokens.textSecondary }}>
      {enabled ? 'Active' : 'Paused'}
    </span>
  )
}

/** The runs tab for one schedule -- time, status, sent/failed counts, and a
 *  link to the archived page. Loaded on demand (DataTable's own expand),
 *  same lazy-fetch shape BudgetRequestsPage's own expand-to-timeline takes. */
function RunsTab({ scheduleId, tokens }: { scheduleId: number; tokens: ThemeTokens }) {
  const styles = pageStyles(tokens)
  const [runs, setRuns] = useState<ReportRunRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const result = await apiJson<{ runs: ReportRunRow[] }>(`/api/reports/runs?scheduleId=${scheduleId}`)
        if (!cancelled) setRuns(result.runs)
      } catch (err) {
        if (!cancelled) setError(errorMessage(err))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [scheduleId])

  if (error) return <p style={styles.error}>{error}</p>
  if (!runs) return <p style={styles.hint}>Loading runs...</p>
  if (runs.length === 0) return <p style={styles.hint}>No runs yet.</p>

  return (
    <table role="presentation" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
      <thead>
        <tr style={{ textAlign: 'left', color: tokens.textSecondary }}>
          <th style={{ padding: '0.3rem 0.6rem' }}>Started</th>
          <th style={{ padding: '0.3rem 0.6rem' }}>Triggered by</th>
          <th style={{ padding: '0.3rem 0.6rem' }}>Status</th>
          <th style={{ padding: '0.3rem 0.6rem' }}>Sent</th>
          <th style={{ padding: '0.3rem 0.6rem' }}>Failed</th>
          <th style={{ padding: '0.3rem 0.6rem' }}>Page</th>
        </tr>
      </thead>
      <tbody>
        {runs.map((r) => (
          <tr key={r.id} style={{ borderTop: `1px solid ${tokens.border}` }}>
            <td style={{ padding: '0.4rem 0.6rem' }}>{fmtDate(r.startedAt)}</td>
            <td style={{ padding: '0.4rem 0.6rem' }}>{r.triggeredBy}</td>
            <td style={{ padding: '0.4rem 0.6rem' }}>
              <StatusBadge status={r.status} tokens={tokens} />
            </td>
            <td style={{ padding: '0.4rem 0.6rem' }}>{r.recipientsSent.length}</td>
            <td style={{ padding: '0.4rem 0.6rem', color: Object.keys(r.recipientsFailed).length ? tokens.danger : undefined }} title={Object.entries(r.recipientsFailed).map(([email, e]) => `${email}: ${e}`).join('\n')}>
              {Object.keys(r.recipientsFailed).length}
            </td>
            <td style={{ padding: '0.4rem 0.6rem' }}>
              {r.url ? (
                <a href={r.url} target="_blank" rel="noreferrer" style={{ color: tokens.brand }}>
                  Open
                </a>
              ) : (
                '-'
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

interface DrawerState {
  mode: 'create' | 'edit'
  schedule?: ReportScheduleRow
}

/** Create/edit drawer: report + team picker, recipients (directory search +
 *  chips), frequency/day/time, timezone. `reportId`/`params.team` are
 *  editable only on CREATE -- editing them on an existing row is a new
 *  schedule, not a respelling of the live one (clients/reportSchedules.ts's
 *  own `updateSchedule` doc explains why). */
function ScheduleDrawer({
  tokens,
  catalog,
  state,
  onClose,
  onSaved,
}: {
  tokens: ThemeTokens
  catalog: ReportCatalogEntry[]
  state: DrawerState
  onClose: () => void
  onSaved: () => void
}) {
  const styles = pageStyles(tokens)
  const editing = state.schedule
  const [reportId, setReportId] = useState(editing?.reportId ?? catalog[0]?.id ?? '')
  const report = catalog.find((r) => r.id === reportId)
  const teamParam = report?.params.find((p) => p.key === 'team')
  const [team, setTeam] = useState(editing?.params.team ?? teamParam?.options[0] ?? '')
  const [recipients, setRecipients] = useState<string[]>(editing?.recipients ?? [])
  const [frequency, setFrequency] = useState<ReportFrequency>(editing?.frequency ?? 'weekdays')
  const [dayOfWeek, setDayOfWeek] = useState<number>(editing?.dayOfWeek ?? 1)
  const [timeOfDay, setTimeOfDay] = useState(editing?.timeOfDay?.slice(0, 5) ?? '07:15')
  const [timezone, setTimezone] = useState(editing?.timezone ?? 'Australia/Sydney')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function addRecipients(users: DirectoryUser[]) {
    const emails = users.map(dirEmail).filter(Boolean)
    setRecipients((prev) => [...new Set([...prev, ...emails.map((e) => e.toLowerCase())])])
  }
  function removeRecipient(email: string) {
    setRecipients((prev) => prev.filter((e) => e !== email))
  }

  async function save() {
    setSaving(true)
    setError(null)
    try {
      const body: Record<string, unknown> = {
        recipients,
        frequency,
        timeOfDay,
        timezone,
        dayOfWeek: frequency === 'weekly' ? dayOfWeek : undefined,
      }
      if (state.mode === 'create') {
        body.reportId = reportId
        body.params = teamParam ? { team } : {}
        await apiJson('/api/reports/schedules', { method: 'POST', body: JSON.stringify(body) })
      } else {
        await apiJson(`/api/reports/schedules/${editing!.id}`, { method: 'PATCH', body: JSON.stringify(body) })
      }
      onSaved()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={styles.modalOverlay} role="presentation" onClick={onClose}>
      <div style={styles.modalCard} role="dialog" aria-modal="true" aria-label="Report schedule" onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.modalTitle}>{state.mode === 'create' ? 'New report schedule' : 'Edit schedule'}</h3>

        {error ? <p style={styles.error}>{error}</p> : null}

        <div style={styles.field}>
          <label style={styles.fieldLabel} htmlFor="report-picker">
            Report
          </label>
          <select
            id="report-picker"
            style={styles.input}
            value={reportId}
            onChange={(e) => setReportId(e.target.value)}
            disabled={state.mode === 'edit'}
          >
            {catalog.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
          {report ? <span style={styles.hint}>{report.description}</span> : null}
        </div>

        {teamParam ? (
          <div style={styles.field}>
            <label style={styles.fieldLabel} htmlFor="team-picker">
              {teamParam.label}
            </label>
            <select id="team-picker" style={styles.input} value={team} onChange={(e) => setTeam(e.target.value)} disabled={state.mode === 'edit'}>
              {teamParam.options.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div style={styles.field}>
          <span style={styles.fieldLabel}>Recipients</span>
          <div style={styles.chipRow}>
            {recipients.length === 0 ? (
              <span style={styles.hint}>No recipients yet.</span>
            ) : (
              recipients.map((email) => (
                <span key={email} style={styles.chip}>
                  {email}
                  <button type="button" aria-label={`Remove ${email}`} style={styles.chipRemove} onClick={() => removeRecipient(email)} disabled={saving}>
                    ×
                  </button>
                </span>
              ))
            )}
          </div>
          <DirectoryPicker
            tokens={tokens}
            multiple
            onPickMany={addRecipients}
            excludeIds={[]}
            placeholder="Search the directory to add a recipient…"
            disabled={saving}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.fieldLabel} htmlFor="frequency-picker">
            Frequency
          </label>
          <select
            id="frequency-picker"
            style={styles.input}
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as ReportFrequency)}
            disabled={saving}
          >
            <option value="weekdays">Weekdays (Mon-Fri)</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </div>

        {frequency === 'weekly' ? (
          <div style={styles.field}>
            <label style={styles.fieldLabel} htmlFor="day-picker">
              Day
            </label>
            <select id="day-picker" style={styles.input} value={dayOfWeek} onChange={(e) => setDayOfWeek(Number(e.target.value))} disabled={saving}>
              {DAY_NAMES.map((name, idx) => (
                <option key={name} value={idx}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: '0.8rem' }}>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.fieldLabel} htmlFor="time-picker">
              Time
            </label>
            <input id="time-picker" type="time" style={styles.input} value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)} disabled={saving} />
          </div>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.fieldLabel} htmlFor="timezone-field">
              Timezone
            </label>
            <input id="timezone-field" style={styles.input} value={timezone} onChange={(e) => setTimezone(e.target.value)} disabled={saving} />
          </div>
        </div>

        <div style={styles.modalActions}>
          <button type="button" style={styles.buttonGhost} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" style={styles.button} onClick={() => void save()} disabled={saving || recipients.length === 0}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function ReportsPage({ tokens }: { tokens: ThemeTokens }) {
  const styles = pageStyles(tokens)
  const [catalog, setCatalog] = useState<ReportCatalogEntry[]>([])
  const [schedules, setSchedules] = useState<ReportScheduleRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [drawer, setDrawer] = useState<DrawerState | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const [catalogRes, schedulesRes] = await Promise.all([
        apiJson<{ reports: ReportCatalogEntry[] }>('/api/reports/catalog'),
        apiJson<{ schedules: ReportScheduleRow[] }>('/api/reports/schedules'),
      ])
      setCatalog(catalogRes.reports)
      setSchedules(schedulesRes.schedules)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function toggleEnabled(row: ReportScheduleRow) {
    setBusyId(row.id)
    setError(null)
    try {
      await apiJson(`/api/reports/schedules/${row.id}/${row.enabled ? 'pause' : 'resume'}`, { method: 'POST' })
      await load()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusyId(null)
    }
  }

  async function sendNow(row: ReportScheduleRow) {
    const label = `${catalog.find((r) => r.id === row.reportId)?.label ?? row.reportId}${row.params.team ? ` (${row.params.team})` : ''}`
    if (!window.confirm(`Send "${label}" now to ${row.recipients.length} recipient(s)? This happens immediately, outside the schedule.`)) {
      return
    }
    setBusyId(row.id)
    setError(null)
    try {
      await apiJson(`/api/reports/schedules/${row.id}/send-now`, { method: 'POST' })
      window.alert('Triggered. The report takes about a minute to gather and send; check the Runs tab shortly.')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusyId(null)
    }
  }

  async function remove(row: ReportScheduleRow) {
    if (!window.confirm(`Delete this schedule? Its run history is kept, but it will never send again.`)) {
      return
    }
    setBusyId(row.id)
    setError(null)
    try {
      await apiJson(`/api/reports/schedules/${row.id}`, { method: 'DELETE' })
      await load()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusyId(null)
    }
  }

  const reportLabel = useMemo(() => {
    const byId = new Map(catalog.map((r) => [r.id, r.label]))
    return (id: string) => byId.get(id) ?? id
  }, [catalog])

  const columns: Array<DataTableColumn<ReportScheduleRow>> = [
    { key: 'report', header: 'Report', render: (r) => reportLabel(r.reportId) },
    { key: 'team', header: 'Team', render: (r) => r.params.team ?? '-' },
    {
      key: 'recipients',
      header: 'Recipients',
      align: 'right',
      render: (r) => (
        <span title={r.recipients.join('\n')}>{r.recipients.length}</span>
      ),
    },
    { key: 'cadence', header: 'Cadence', render: (r) => cadenceLabel(r) },
    { key: 'enabled', header: 'Status', render: (r) => <EnabledBadge enabled={r.enabled} tokens={tokens} /> },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) => (
        <div style={styles.actionsCell}>
          <button type="button" style={styles.buttonGhost} onClick={() => setDrawer({ mode: 'edit', schedule: r })} disabled={busyId === r.id}>
            Edit
          </button>
          <button type="button" style={styles.buttonGhost} onClick={() => void toggleEnabled(r)} disabled={busyId === r.id}>
            {r.enabled ? 'Pause' : 'Resume'}
          </button>
          <button type="button" style={styles.buttonGhost} onClick={() => void sendNow(r)} disabled={busyId === r.id}>
            Send now
          </button>
          <button type="button" style={styles.buttonDanger} onClick={() => void remove(r)} disabled={busyId === r.id}>
            Delete
          </button>
        </div>
      ),
    },
  ]

  return (
    <div style={styles.page}>
      <div style={styles.statsRow}>
        <StatCard label="Schedules" value={schedules.length} tokens={tokens} />
        <StatCard label="Active" value={schedules.filter((s) => s.enabled).length} tokens={tokens} />
      </div>

      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <h2 style={styles.sectionTitle}>Reports</h2>
            <p style={styles.sectionSubtitle}>
              Scheduled sends of the Underwriting Team View. Every seeded schedule starts paused; enable one only after checking its
              recipients and cadence. Cadence is shown in the schedule's own timezone, not yours.
            </p>
          </div>
          <div style={styles.toolbar}>
            <button type="button" style={styles.button} onClick={() => setDrawer({ mode: 'create' })} disabled={catalog.length === 0}>
              New schedule
            </button>
          </div>
        </div>

        {error ? <p style={styles.error}>{error}</p> : null}
        {loading ? (
          <p style={styles.hint}>Loading...</p>
        ) : (
          <DataTable
            columns={columns}
            rows={schedules}
            getRowKey={(r) => String(r.id)}
            getRowLabel={(r) => `${reportLabel(r.reportId)} ${r.params.team ?? ''}`}
            tokens={tokens}
            emptyLabel="No report schedules yet."
            onRowActivate={(r) => setExpandedId((cur) => (cur === r.id ? null : r.id))}
            isRowExpanded={(r) => expandedId === r.id}
            renderExpanded={(r) => <RunsTab scheduleId={r.id} tokens={tokens} />}
          />
        )}
      </div>

      {drawer ? (
        <ScheduleDrawer
          tokens={tokens}
          catalog={catalog}
          state={drawer}
          onClose={() => setDrawer(null)}
          onSaved={() => {
            setDrawer(null)
            void load()
          }}
        />
      ) : null}
    </div>
  )
}
